import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'
import Loading from '../../loading/loadingPage'
import { downloadPdf } from '../../../helpers/downloadPdf'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import useTrackedItems from './useTrackedItems'
import RestockQueueRow from './RestockQueueRow'
import ConsolidatedPickList from './ConsolidatedPickList'

const buildAggregates = (trackedItems) => {
  const needed = new Map()
  const comingDue = new Map()

  trackedItems.forEach((item) => {
    item.missingItems.forEach((mi) => {
      const key = `${mi.name}|${mi.size}`
      if (!needed.has(key)) {
        needed.set(key, {
          name: mi.name,
          size: mi.size,
          noExpiry: mi.noExpiry,
          total: 0,
          sources: [],
        })
      }
      const entry = needed.get(key)
      entry.total += mi.missing
      entry.sources.push({
        trackedItemId: item.id,
        label: item.label,
        kind: item.kind,
        sectionName: mi.section,
        missing: mi.missing,
      })
    })

    // expiringSoonItems doesn't carry a sectionName (the model virtual
    // flattens sections away), so this is informational only - no inline
    // restock action here, unlike the "needed" rows above.
    item.expiringSoonItems.forEach((ei) => {
      const key = `${ei.name}|${ei.size}`
      if (!comingDue.has(key)) {
        comingDue.set(key, {
          name: ei.name,
          size: ei.size,
          total: 0,
          sources: [],
        })
      }
      const entry = comingDue.get(key)
      entry.total += 1
      entry.sources.push({ label: item.label, expiryDate: ei.expiryDate })
    })
  })

  return { needed: [...needed.values()], comingDue: [...comingDue.values()] }
}

const RestockQueue = () => {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const { trackedItems, setTrackedItems, loading, fetchError } =
    useTrackedItems()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('mostNeeded')
  const [pdfLoading, setPdfLoading] = useState(false)
  const [claims, setClaims] = useState([])

  const { needed, comingDue } = useMemo(
    () => buildAggregates(trackedItems),
    [trackedItems],
  )

  const claimsByKey = useMemo(
    () => new Map(claims.map((c) => [c.key, c])),
    [claims],
  )

  const loadClaims = () => {
    API.get('/makeready/restock-claims')
      .then((res) => {
        if (res.status === 200) setClaims(res.data.claims || [])
      })
      .catch((e) => console.error(e))
  }

  useEffect(() => {
    loadClaims()
  }, [])

  const handleClaim = (key) =>
    API.post('/makeready/restock-claims/claim', { key })
      .then((res) => {
        setClaims((prev) => [
          ...prev.filter((c) => c.key !== key),
          res.data.claim,
        ])
      })
      .catch((e) => {
        // 409 means someone else claimed it first - reflect their claim.
        if (e.response?.status === 409 && e.response.data?.claim) {
          const claim = e.response.data.claim
          setClaims((prev) => [...prev.filter((c) => c.key !== key), claim])
        } else {
          console.error(e)
        }
      })

  const handleRelease = (key) =>
    API.post('/makeready/restock-claims/release', { key })
      .then(() => setClaims((prev) => prev.filter((c) => c.key !== key)))
      .catch((e) => console.error(e))

  const visibleNeeded = useMemo(() => {
    let visible = needed
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      visible = visible.filter((e) => e.name.toLowerCase().includes(q))
    }
    return [...visible].sort((a, b) =>
      sort === 'label' ? a.name.localeCompare(b.name) : b.total - a.total,
    )
  }, [needed, search, sort])

  const handleRestocked = ({ name, size, source, quantity }) => {
    setTrackedItems((prev) =>
      prev.map((item) => {
        if (item.id !== source.trackedItemId) return item
        const missingItems = item.missingItems
          .map((mi) =>
            mi.name === name && mi.size === size
              ? { ...mi, missing: mi.missing - quantity }
              : mi,
          )
          .filter((mi) => mi.missing > 0)
        return { ...item, missingItems, missingCount: missingItems.length }
      }),
    )
    loadClaims()
  }

  return (
    <Stack gap={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="title2">Make Ready — Restock Queue</Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/make-ready-overview')}
        >
          Back to Search
        </Button>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} display="flex">
          <TextField
            size="small"
            label="Search item name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 220, flex: 0.6 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              label="Sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <MenuItem value="mostNeeded">Most needed first</MenuItem>
              <MenuItem value="label">Item name (A-Z)</MenuItem>
            </Select>
          </FormControl>
          {visibleNeeded.length > 0 && (
            <Button
              variant="contained"
              disabled={pdfLoading}
              sx={{ whiteSpace: 'nowrap', px: 1, flex: 0.4 }}
              onClick={async () => {
                setPdfLoading(true)
                try {
                  await downloadPdf(
                    <ConsolidatedPickList entries={visibleNeeded} />,
                    'consolidated-pick-list.pdf',
                  )
                } catch (e) {
                  console.error(e)
                } finally {
                  setPdfLoading(false)
                }
              }}
            >
              {pdfLoading ? 'Preparing PDF...' : 'Download Pick List'}
            </Button>
          )}
        </Stack>
      </Paper>

      {loading && <Loading hideText />}
      {!loading && fetchError && (
        <Alert severity="error">
          Unable to load the restock queue right now. Please try again later.
        </Alert>
      )}

      {!loading && !fetchError && (
        <>
          <Stack gap={1.5}>
            <Typography variant="title2" fontSize="1rem">
              Needed Now ({visibleNeeded.length})
            </Typography>
            {visibleNeeded.length === 0 ? (
              <Alert severity="success">
                Nothing outstanding across the tracked fleet.
              </Alert>
            ) : (
              visibleNeeded.map((entry) => (
                <RestockQueueRow
                  key={`${entry.name}|${entry.size}`}
                  entry={entry}
                  onRestocked={handleRestocked}
                  claimsByKey={claimsByKey}
                  onClaim={handleClaim}
                  onRelease={handleRelease}
                  currentUserId={user?._id}
                />
              ))
            )}
          </Stack>

          {comingDue.length > 0 && (
            <Stack gap={1} sx={{ mt: 2 }}>
              <Typography variant="title2" fontSize="1rem">
                Coming Due ({comingDue.length})
              </Typography>
              <Alert severity="info" variant="outlined">
                Not yet missing - still in date, but expiring within 30 days
                across the fleet.
              </Alert>
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                gap={1.5}
              >
                {comingDue.map((entry) => (
                  <Paper key={`${entry.name}|${entry.size}`} sx={{ p: 1.5 }}>
                    <Typography fontWeight={700}>
                      {entry.name} {entry.size && `(${entry.size})`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.sources
                        .map(
                          (s) =>
                            `${s.label}: ${moment(s.expiryDate).format('DD/MM/YYYY')}`,
                        )
                        .join(' · ')}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}

export default RestockQueue
